import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

/** Nagy oldalcím, ahogy egy mobilappban: a tartalom tetején, nem egy sávban. */
export const PageTitle: React.FC<{ title: string; subtitle?: React.ReactNode; action?: React.ReactNode }> = ({
  title,
  subtitle,
  action,
}) => (
  <div className="mb-5 flex items-end justify-between gap-3 pt-2">
    <div className="min-w-0">
      <h1 className="text-[28px] font-bold leading-tight tracking-tight text-gray-900">{title}</h1>
      {subtitle && <p className="mt-0.5 text-[15px] text-gray-500">{subtitle}</p>}
    </div>
    {action}
  </div>
);

/** Aloldal fejléce: vissza gomb és cím. */
export const BackHeader: React.FC<{ title?: string; back?: string; backLabel?: string }> = ({
  title,
  back,
  backLabel = 'Vissza',
}) => {
  const navigate = useNavigate();
  return (
    <div className="mb-3 flex items-center gap-1 pt-1">
      <button
        type="button"
        onClick={() => (back ? navigate(back) : window.history.length > 1 ? navigate(-1) : navigate('/'))}
        className="-ml-2 flex h-11 items-center gap-0.5 rounded-lg pr-3 text-[17px] text-primary-600 active:opacity-60"
      >
        <ChevronLeftIcon className="h-6 w-6" />
        {backLabel}
      </button>
      {title && <span className="truncate text-[17px] font-semibold text-gray-900">{title}</span>}
    </div>
  );
};

/** Szekció cím a csoportok felett. */
export const SectionLabel: React.FC<{ children: React.ReactNode; action?: React.ReactNode }> = ({ children, action }) => (
  <div className="mb-2 mt-7 flex items-baseline justify-between px-1">
    <h2 className="text-[13px] font-semibold uppercase tracking-wide text-gray-500">{children}</h2>
    {action}
  </div>
);

/** Fehér, lekerekített csoport elválasztó vonalakkal (mint a telefon beállításai). */
export const Group: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`overflow-hidden rounded-2xl bg-white ring-1 ring-black/5 ${className}`}>
    <div className="divide-y divide-gray-100">{children}</div>
  </div>
);

interface RowProps {
  to?: string;
  href?: string;
  onClick?: () => void;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  value?: React.ReactNode;
  tone?: 'default' | 'danger';
  chevron?: boolean;
}

/** Egy sor a csoportban: ikon, cím, alcím, jobb oldalon érték és nyíl. */
export const Row: React.FC<RowProps> = ({ to, href, onClick, icon: Icon, title, subtitle, value, tone = 'default', chevron }) => {
  const showChevron = chevron ?? Boolean(to || onClick);
  const content = (
    <div className="flex min-h-[52px] items-center gap-3 px-4 py-3">
      {Icon && <Icon className={`h-6 w-6 shrink-0 ${tone === 'danger' ? 'text-red-500' : 'text-gray-400'}`} />}
      <div className="min-w-0 flex-1">
        <div className={`text-[16px] ${tone === 'danger' ? 'text-red-600' : 'text-gray-900'}`}>{title}</div>
        {subtitle && <div className="mt-0.5 text-[13px] text-gray-500">{subtitle}</div>}
      </div>
      {value && <div className="shrink-0 text-[15px] text-gray-500">{value}</div>}
      {showChevron && <ChevronRightIcon className="h-5 w-5 shrink-0 text-gray-300" />}
    </div>
  );
  const cls = 'block w-full text-left transition-colors active:bg-gray-50';
  if (to) return <Link to={to} className={cls}>{content}</Link>;
  if (href) return <a href={href} className={cls} target="_blank" rel="noreferrer">{content}</a>;
  if (onClick) return <button type="button" onClick={onClick} className={cls}>{content}</button>;
  return content;
};

/** Töltés közben: halvány helykitöltők a lista helyén. */
export const LoadingRows: React.FC<{ rows?: number }> = ({ rows = 3 }) => (
  <Group>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex animate-pulse items-center gap-3 px-4 py-4">
        <div className="h-9 w-12 rounded-lg bg-gray-100" />
        <div className="flex-1 space-y-2">
          <div className="h-3.5 w-2/3 rounded bg-gray-100" />
          <div className="h-3 w-1/3 rounded bg-gray-100" />
        </div>
      </div>
    ))}
  </Group>
);
